<template>
    <DefaultHeader />

    <div class="container-fluid">
        <div class="row d-flex justify-content-center">
            <div class="col-11 mb-3 mb-lg-5">
                <div class="card table-nowrap table-card">
                    <div class="table-responsive">
                        <table class="table table-striped mb-0">

                            <thead class="small text-uppercase bg-body text-muted">

                                <!-- Bouton d'ouverture de la création -->
                                <tr v-if="!showCreateUser">
                                    <td colspan="6" class="text-end">
                                        <button
                                            class="btn btn-primary"
                                            @click="openCreateUser"
                                        >
                                            <font-awesome-icon
                                                :icon="['fas', 'circle-plus']"
                                            />

                                            Ajouter un utilisateur
                                        </button>
                                    </td>
                                </tr>

                                <!-- Formulaire de création -->
                                <template v-else>

                                    <tr class="align-middle">

                                        <td>
                                            <div class="form-group">
                                                <input
                                                    v-model="email"
                                                    type="email"
                                                    class="form-control"
                                                    autocomplete="off"
                                                    placeholder="Adresse e-mail"
                                                >
                                            </div>
                                        </td>

                                        <td>
                                            <div class="form-group">
                                                <input
                                                    v-model="password"
                                                    type="password"
                                                    class="form-control"
                                                    placeholder="Mot de passe"
                                                >
                                            </div>
                                        </td>

                                        <td>
                                            <div class="form-group">
                                                <select
                                                    v-model="role"
                                                    class="form-select"
                                                >
                                                    <option value="clientviewer">
                                                        Visiteur
                                                    </option>

                                                    <option value="clientoperator">
                                                        Utilisateur
                                                    </option>

                                                    <option value="clientadmin">
                                                        Administrateur
                                                    </option>
                                                </select>
                                            </div>
                                        </td>

                                        <td>
                                            <div class="form-group">
                                                <select
                                                    v-model="vehicleVisibility"
                                                    class="form-select"
                                                >
                                                    <option value="ALL">
                                                        Tous les véhicules
                                                    </option>

                                                    <option value="GROUPS">
                                                        Groupes uniquement
                                                    </option>
                                                </select>
                                            </div>
                                        </td>

                                        <!-- Groupes -->
                                        <td>
                                            <div
                                                v-if="vehicleVisibility === 'GROUPS'"
                                                class="d-flex flex-wrap gap-2"
                                            >
                                                <div
                                                    v-for="group in groups"
                                                    :key="group.id"
                                                    class="form-check"
                                                >
                                                    <input
                                                        :id="'new-user-group-' + group.id"
                                                        v-model="newUserGroups"
                                                        :value="group.id"
                                                        type="checkbox"
                                                        class="form-check-input"
                                                    >

                                                    <label
                                                        class="form-check-label text-body text-capitalize"
                                                        :for="'new-user-group-' + group.id"
                                                    >
                                                        {{ group.name }}
                                                    </label>
                                                </div>
                                            </div>
                                        </td>

                                        <!-- Actions -->
                                        <td class="text-end">
                                            <div class="d-flex justify-content-end gap-2">

                                                <button
                                                    class="btn btn-primary"
                                                    :disabled="creatingUser"
                                                    @click="createUser"
                                                >
                                                    Valider
                                                </button>

                                                <button
                                                    class="btn btn-danger"
                                                    :disabled="creatingUser"
                                                    @click="closeCreateUser"
                                                >
                                                    Annuler
                                                </button>

                                            </div>
                                        </td>

                                    </tr>

                                </template>

                            </thead>

                            <tbody>

                                <tr>
                                    <th>Utilisateur</th>
                                    <th>Dernière connexion</th>
                                    <th>Niveau d'accès</th>
                                    <th>Visibilité</th>
                                    <th>Groupes autorisés</th>
                                    <th class="text-end"></th>
                                </tr>

                                <TableRow
                                    v-for="user in users"
                                    :key="user.id"
                                    :user="user"
                                    :groups="groups"
                                    @refresh="refreshData"
                                />

                            </tbody>

                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <DefaultFooter />
</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue'
import DefaultFooter from '@/components/DefaultFooter.vue'
import TableRow from './components/TableRow.vue'

import UserService from '@/services/user.service'
import GroupService from '@/services/group.service'
import AuthService from '@/services/auth.service'

export default {
    components: {
        DefaultHeader,
        DefaultFooter,
        TableRow,
    },

    data() {
        return {
            users: [],
            groups: [],

            showCreateUser: false,
            creatingUser: false,

            email: '',
            password: '',
            role: 'clientviewer',

            vehicleVisibility: 'ALL',
            newUserGroups: []
        };
    },

    mounted() {
        this.refreshData();
        this.getAllGroups();
    },

    methods: {

        async refreshData() {
            try {
                const response = await UserService.getClientUsers();

                this.users = response.data || [];

                return this.users;

            } catch (e) {
                console.log(e);

                return [];
            }
        },

        openCreateUser() {
            this.resetCreateForm();

            this.showCreateUser = true;
        },

        closeCreateUser() {
            this.resetCreateForm();

            this.showCreateUser = false;
        },

        async createUser() {
            if (!this.email || !this.password || !this.role) {
                return;
            }

            this.creatingUser = true;

            const email = this.email.trim();
            const password = this.password;
            const role = this.role;

            const vehicleVisibility =
                this.vehicleVisibility;

            const selectedGroups =
                [...this.newUserGroups];

            try {

                const response = await AuthService.createUser(
                    email,
                    password,
                    role
                );

                let userId =
                    response?.data?.id ??
                    response?.data?.user?.id ??
                    null;

                /*
                 * Si createUser ne retourne pas l'ID,
                 * on retrouve le compte créé dans la liste.
                 */
                if (!userId) {

                    const users =
                        await this.refreshData();

                    const createdUser = users.find(
                        user =>
                            user.email?.toLowerCase() ===
                            email.toLowerCase()
                    );

                    userId =
                        createdUser?.id ?? null;
                }

                if (!userId) {
                    throw new Error(
                        "Impossible de récupérer l'utilisateur créé."
                    );
                }

                /*
                 * Application du mode de visibilité.
                 */
                await UserService.updateVehicleVisibility(
                    userId,
                    vehicleVisibility
                );

                /*
                 * Les groupes ne sont configurés que pour GROUPS.
                 */
                if (vehicleVisibility === 'GROUPS') {

                    await UserService.updateGroups(
                        userId,
                        selectedGroups
                    );
                }

                await this.refreshData();

                /*
                 * Après création réussie :
                 * on vide et on referme le formulaire.
                 */
                this.closeCreateUser();

            } catch (e) {
                console.log(e);

            } finally {
                this.creatingUser = false;
            }
        },

        resetCreateForm() {
            this.email = '';
            this.password = '';
            this.role = 'clientviewer';

            this.vehicleVisibility = 'ALL';
            this.newUserGroups = [];
        },

        getAllGroups() {
            GroupService.getClientGroups()
                .then(groupList => {
                    this.groups =
                        groupList.data || [];
                })
                .catch(e => {
                    console.log(e);
                });
        }

    }
}
</script>